import { useLocation, useNavigate, useParams } from 'react-router';

export interface UrlObject {
  pathname?: string;
}

export type Url = string | UrlObject;

export interface RouterEvents {
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
  emit: (event: string, ...args: unknown[]) => void;
}

export interface NextCompatRouter {
  push: (url: Url) => void;
  replace: (url: Url) => void;
  back: () => void;
  reload: () => void;
  prefetch: () => Promise<void>;
  pathname: string;
  asPath: string;
  query: Record<string, string | undefined>;
  isReady: true;
  events: RouterEvents;
  locale: string | undefined;
  locales: string[] | undefined;
  defaultLocale: string | undefined;
}

const toUrl = (url: Url): string =>
  typeof url === 'string' ? url : url?.pathname ?? '/';

export function useRouter(): NextCompatRouter {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<Record<string, string | undefined>>();

  const splat = params['*'];
  const query: Record<string, string | undefined> = { ...params };
  if (splat !== undefined && query.id === undefined) {
    query.id = splat;
  }

  const search = new URLSearchParams(location.search);
  for (const [k, v] of search) query[k] = v;

  return {
    push: (url) => navigate(toUrl(url)),
    replace: (url) => navigate(toUrl(url), { replace: true }),
    back: () => navigate(-1),
    reload: () => window.location.reload(),
    prefetch: () => Promise.resolve(),
    pathname: location.pathname,
    asPath: location.pathname + location.search + location.hash,
    query,
    isReady: true,
    events: { on: () => {}, off: () => {}, emit: () => {} },
    locale: undefined,
    locales: undefined,
    defaultLocale: undefined,
  };
}
